import { Component, DestroyRef, OnInit, effect, inject, input, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ValidatorFn, Validators } from '@angular/forms';
import { BaseFieldComponent } from '../base-field/base-field.component';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import {
	SearchableDropdownControlComponent,
	SearchableDropdownOption,
} from '../../../controls/standard/searchable-dropdown-control/searchable-dropdown-control.component';
import { StrideReports } from '../../../../services/stride-reports';

@Component({
	selector: 'app-stride-log-student-field',
	standalone: true,
	imports: [AppModule, ZorroModule, ThirdPartyModule, AngularModule, SearchableDropdownControlComponent],
	templateUrl: './stride-log-student-field.component.html',
	styleUrl: './stride-log-student-field.component.scss',
})
export class StrideLogStudentFieldComponent extends BaseFieldComponent implements OnInit {
	public static override validators: ValidatorFn[] = [Validators.required];

	public clientUuid = input<string | undefined>(undefined);

	public options = signal<SearchableDropdownOption[]>([]);
	public loading = signal<boolean>(false);

	private searchTerms = new Subject<string>();
	private destroyRef = inject(DestroyRef);

	constructor(private strideReports: StrideReports) {
		super();

		// Re-run the default (empty search) whenever the admin picks a different client.
		effect(() => {
			if (this.clientUuid()) {
				this.searchTerms.next('');
			}
		});
	}

	ngOnInit(): void {
		const sub = this.searchTerms
			.pipe(
				debounceTime(300),
				switchMap(async search => {
					const clientUuid = this.clientUuid();
					if (!clientUuid) {
						return { students: [] };
					}

					this.loading.set(true);
					try {
						return await this.strideReports.getStrideLogStudents$(clientUuid, search || undefined);
					} catch (err) {
						console.error('Failed to load stride log students', err);
						return { students: [] };
					}
				})
			)
			.subscribe(result => {
				this.options.set(
					result.students.map(student => ({
						value: student.studentUuid,
						label: student.displayName,
					}))
				);
				this.loading.set(false);
			});

		this.destroyRef.onDestroy(() => sub.unsubscribe());
	}

	public onSearch(term: string): void {
		this.searchTerms.next(term);
	}
}
